import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TitleHolder } from "../../common/sharedComponents/styles/card.style";
import { StepperContentHolder } from "../vendorRegistration/vendorRegistration";
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Chip,
  Box,
  Tooltip,
  CardContent,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  CustomTableContainer,
  CustomTableHead,
  TableHeaderFont,
} from "../../styles/tableContent.style";
import moment from "moment";
import { getVendorRequestsThunk, uploadVendorDocThunk } from "./redux/documentRequestsSlice";
import { submitVendorEdit } from "../vendorRegistration/redux/vendorRegistrationSlice";
import { history } from "../../app/history";
import LocalStorage from "../../utilities/localStorage";
import { StyledCard, StyledOutlineButton } from "../vendorRegistration/components/StepMain";
import { VendorCardTitle, VendorListHolder } from "../vendorRegistration/components/steps/vendorReg";
import apiClient from "../../utilities/apiClient";
import { message } from "antd";

const VendorDocumentRequests = ({
  isStepper = false,
  handleBack,
  activeStep,
  setActiveStep,
  isLocked = false,
  isCreation = false,
  vendor_id,
  skipped,
  setSkipped,
  isStepSkipped,
}) => {
  const dispatch = useDispatch();

  const { vendorRequests, loading } = useSelector((state) => state.documentRequests);

  const [localUploading, setLocalUploading] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const client = LocalStorage.getItem("client");
  const isTenant2 = client?.id === 2;

  const fetchRequestsList = () => {
    dispatch(getVendorRequestsThunk());
  };

  useEffect(() => {
    fetchRequestsList();
    // eslint-disable-next-line
  }, []);

  const handleFileChange = async (event, mappingId) => {
    const target = event.target;
    const file = target ? target.files[0] : null;
    if (!file) return;

    // Optional validation (e.g. max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error("File size exceeds 10MB limit.");
      if (target) target.value = "";
      return;
    }

    setLocalUploading(true);
    message.loading({ content: "Uploading file to S3...", key: "uploading" });

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Call S3 Upload API directly
      const response = await apiClient.post(
        apiClient.Urls.upload_gst_doc,
        formData,
        true,
        "file"
      );

      if (response && response.success && response.data) {
        const fileUrl = response.data.Location;
        const fileName = response.data.file_name || file.name;

        // Submit to our vendor document mapping endpoint
        dispatch(
          uploadVendorDocThunk({
            vendor_document_request_id: mappingId,
            uploaded_file_url: fileUrl,
            uploaded_file_name: fileName,
          })
        ).then((res) => {
          setLocalUploading(false);
          message.destroy("uploading");
          if (res?.payload?.success) {
            fetchRequestsList();
          }
        });
      } else {
        setLocalUploading(false);
        message.destroy("uploading");
        message.error(response?.message || "Error uploading file to storage");
      }
    } catch (err) {
      setLocalUploading(false);
      message.destroy("uploading");
      message.error("An unexpected error occurred during upload");
      console.error(err);
    }

    // Reset input
    if (target) target.value = "";
  };

  const handleRemoveClick = (mappingId) => {
    setLocalUploading(true);
    message.loading({ content: "Removing document...", key: "removing" });

    dispatch(
      uploadVendorDocThunk({
        vendor_document_request_id: mappingId,
        uploaded_file_url: "",
        uploaded_file_name: "",
      })
    ).then((res) => {
      setLocalUploading(false);
      message.destroy("removing");
      if (res?.payload?.success) {
        fetchRequestsList();
      }
    });
  };

  const handleFinish = () => {
    const user = LocalStorage.getItem("tata_login_user");
    const primary_role = user.roles[0];

    const currentVendorId = vendor_id || user?.vendor_id || user?.id;

    if (!currentVendorId) {
      message.error("Vendor ID is missing. Please refresh the page and try again.");
      return;
    }

    // In creation mode, the bank and documents are already saved — just navigate to /app.
    // submitVendorEdit is only for submitting pending edits on an existing vendor.
    if (isCreation) {
      message.success("Registration completed successfully!");
      history.push("/app");
      return;
    }

    const payload = {
      vendor_id: currentVendorId,
      is_finish: isTenant2 ? true : false,
    };

    setIsFinishing(true);
    dispatch(submitVendorEdit(payload))
      .unwrap()
      .then((res) => {
        setIsFinishing(false);
        if (res?.success) {
          message.success(res?.message || "Edits submitted successfully.");
        } else {
          message.info(res?.message || "No pending edits to submit.");
        }
        if (primary_role === "user") {
          history.push("/app");
        } else {
          let newSkipped = skipped;
          if (isStepSkipped && isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
          }
          if (setActiveStep) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
          }
          if (setSkipped) {
            setSkipped(newSkipped);
          }
        }
      })
      .catch((err) => {
        setIsFinishing(false);
        message.error("Failed to submit edits.");
      });
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "APPROVED":
        return <Chip label="Approved" color="success" size="small" />;
      case "REJECTED":
        return <Chip label="Rejected" color="error" size="small" />;
      case "UPLOADED":
        return <Chip label="Submitted" color="warning" size="small" />;
      default:
        return <Chip label="Pending Action" color="default" size="small" />;
    }
  };

  const content = (
    <>
      {loading && vendorRequests.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <CircularProgress />
        </div>
      ) : (
        <CustomTableContainer component={Card}>
          <Table aria-label="simple table">
            <CustomTableHead>
              <TableRow>
                <TableCell>
                  <TableHeaderFont>Sr. No.</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Document Name</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Description / Instructions</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Due Date</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Required</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Status</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Submitted File</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Feedback / Remarks</TableHeaderFont>
                </TableCell>
                <TableCell align="center">
                  <TableHeaderFont>Action</TableHeaderFont>
                </TableCell>
              </TableRow>
            </CustomTableHead>

            <TableBody>
              {vendorRequests && vendorRequests.length > 0 ? (
                vendorRequests.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {index + 1}
                    </TableCell>
                    <TableCell style={{ fontWeight: 500 }}>
                      {item.DocumentRequest ? item.DocumentRequest.document_name : "-"}
                    </TableCell>
                    <TableCell style={{ maxWidth: "250px", wordBreak: "break-word" }}>
                      {item.DocumentRequest ? item.DocumentRequest.description || "No description instructions." : "-"}
                    </TableCell>
                    <TableCell>
                      {item.DocumentRequest && item.DocumentRequest.due_date
                        ? moment(item.DocumentRequest.due_date).format("DD MMM YYYY")
                        : "No Limit"}
                    </TableCell>
                    <TableCell>
                      {item.DocumentRequest && item.DocumentRequest.is_required ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                    <TableCell>
                      {item.uploaded_file_url ? (
                        <a
                          href={item.uploaded_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          {item.uploaded_file_name || "View Submitted File"}
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell style={{ color: "#c62828", fontSize: "0.85rem", fontWeight: 500 }}>
                      {item.comments || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {isLocked ? (
                        <span style={{ color: "#888", fontSize: "0.85rem" }}>
                          {item.status === "APPROVED" ? "Completed" : "Under Review"}
                        </span>
                      ) : item.uploaded_file_url && !(item.pendingChange && item.pendingChange.new_value === null) ? (
                        <span style={{ display: "inline-flex", alignItems: "center" }}>
                          <HighlightOffIcon
                            sx={{ color: "#4945FF", cursor: "pointer" }}
                            fontSize="medium"
                            onClick={() => handleRemoveClick(item.id)}
                          />
                          <span
                            style={{ marginLeft: "4px", fontSize: "0.85rem", color: "#4945FF", cursor: "pointer" }}
                            onClick={() => handleRemoveClick(item.id)}
                          >
                            Remove
                          </span>
                        </span>
                      ) : (
                        <label htmlFor={`requested_doc_${item.id}`}>
                          <input
                            type="file"
                            id={`requested_doc_${item.id}`}
                            name={`requested_doc_${item.id}`}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                            onChange={(e) => handleFileChange(e, item.id)}
                            disabled={localUploading}
                          />
                          <StyledOutlineButton
                            variant="outlined"
                            component="span"
                            disabled={localUploading}
                            style={{ textTransform: "capitalize" }}
                          >
                            {item.status === "REJECTED" ? "Re-upload" : "Upload"}
                          </StyledOutlineButton>
                        </label>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ padding: "2rem" }}>
                    No document requests found. You're all caught up!
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CustomTableContainer>
      )}
    </>
  );

  if (isStepper) {
    return (
      <Box>
        <StyledCard>
          <CardContent>
            <VendorCardTitle>Step 4: Requested Documents</VendorCardTitle>
            <VendorListHolder>
              <li>Upload all requested documents here to complete your application.</li>
              <li>Required documents must be uploaded before submitting.</li>
            </VendorListHolder>
          </CardContent>
        </StyledCard>

        {content}

        <Box sx={{ display: "flex", flexDirection: "row", pt: 2, justifyContent: "space-between" }}>
          <Button color="inherit" onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
          <Tooltip title="" arrow placement="top">
            <span>
              <LoadingButton
                sx={{ textTransform: "none", background: "var(--button-color)", width: "200px" }}
                color="success"
                variant="contained"
                onClick={handleFinish}
                disabled={isLocked}
                loading={isFinishing}
              >
                {isLocked ? "Submitted" : "Finish"}
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>

      </Box>
    );
  }

  return (
    <div>
      <TitleHolder style={{ paddingBottom: "0" }}>
        Requested Documents & Uploads
      </TitleHolder>
      <br />
      <StepperContentHolder>
        {content}
      </StepperContentHolder>

    </div>
  );
};

export default VendorDocumentRequests;
